import React, { createContext, useContext, useMemo, useState } from 'react';
import { authApi } from '../services/authApi';

interface AuthContextValue {
  isAuthenticated: boolean;
  username: string | null;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => authApi.getToken());
  const [username, setUsername] = useState<string | null>(() => authApi.getUsername());

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(token),
      username,
      login: async (loginUsername: string, password: string) => {
        const result = await authApi.login({ username: loginUsername, password });

        if (!result.success || !result.token || !result.username) {
          return {
            success: false,
            error: result.message || 'Invalid username or password',
          };
        }

        authApi.saveSession(result.token, result.username);
        setToken(result.token);
        setUsername(result.username);

        return { success: true };
      },
      logout: () => {
        authApi.clearSession();
        setToken(null);
        setUsername(null);
      },
    }),
    [token, username]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
};
